import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { imageContentType, videoContentType } from '../../shared/attachmentTypes';
import { attachmentPreviewSize } from '../../shared/attachmentPreview';
import { fmtBytes, fmtDate } from './components';
import { t } from './i18n';
import type { Attachment } from './types';
import { useMeta } from './meta';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { afterId, useDragReorder } from './dragReorder';

/** Files from a paste (e.g. a screenshot); a pasted "image.png" gets a unique name. */
export function pastedFiles(data: DataTransfer | null | undefined): File[] {
  const stamp = Date.now();
  return [...(data?.files ?? [])].map((f, i) => (f.name === 'image.png' ? new File([f], `grafik-${stamp}-${i}.png`, { type: f.type }) : f));
}

/** One file waiting to be uploaded: a thumbnail for images and videos (from the local file), name, size, remove. */
function PendingFile({ file, onRemove }: { file: File; onRemove: () => void }) {
  const kind = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : null;
  const [url, setUrl] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    if (!kind) return;
    const u = URL.createObjectURL(file);
    setUrl(u);
    setBroken(false);
    return () => URL.revokeObjectURL(u);
  }, [file, kind]);
  const thumb = kind && url && !broken;
  return (
    <span className={thumb ? 'pending-file has-thumb' : 'tag'} title={file.name}>
      {thumb && (
        <span className="pending-thumb">
          {kind === 'image'
            ? <img src={url} alt="" onError={() => setBroken(true)} />
            : <><video src={`${url}#t=0.1`} muted preload="metadata" playsInline onError={() => setBroken(true)} /><span className="pending-play" aria-hidden>▶</span></>}
        </span>
      )}
      <span className="pending-name">
        <span className="pending-label">{file.name} <span className="subtle small">({fmtBytes(file.size)})</span></span>
        <button type="button" className="tag-x" aria-label={t('Remove')} onClick={onRemove}>×</button>
      </span>
    </span>
  );
}

/** Files chosen, pasted or dropped but not uploaded yet. */
export function PendingFiles({ files, onChange }: { files: File[]; onChange: (files: File[]) => void }) {
  if (!files.length) return null;
  return (
    <div className="pending-files">
      {files.map((f, i) => <PendingFile key={`${i}:${f.name}:${f.size}:${f.lastModified}`} file={f} onRemove={() => onChange(files.filter((_, j) => j !== i))} />)}
    </div>
  );
}

const fileUrl = (a: Attachment, inline = false) => `/api/attachments/${a.id}${inline ? '?inline=1' : ''}`;
const isImage = (a: Attachment) => a.available && imageContentType(a.file_type) !== null;
const isVideo = (a: Attachment) => a.available && videoContentType(a.file_type, a.filename) !== null;

function VideoPreview({ file, onClose }: { file: Attachment; onClose: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const [compatible, setCompatible] = useState(false);
  const preparation = useQuery({
    queryKey: ['video-preview', file.id],
    queryFn: () => api.get<{ state: 'processing' | 'ready' }>(`/api/attachments/${file.id}/video-preview`),
    enabled: compatible && !failed,
    retry: false,
    refetchInterval: (query) => query.state.data?.state === 'processing' ? 1000 : false,
  });
  const ready = !compatible || preparation.data?.state === 'ready';
  const error = failed || (compatible && preparation.isError);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <>
      {error ? <p className="small" role="alert">{t('This video could not be played in your browser. Download it to watch it in another player.')}</p> : !ready ? (
        <p className="small" role="status">{t('Preparing a compatible video preview…')}</p>
      ) : (
        <video key={compatible ? 'compatible' : 'original'} ref={ref} className="attachment-video" src={compatible ? `/api/attachments/${file.id}?preview=1` : fileUrl(file, true)} controls autoPlay playsInline preload="metadata"
          aria-label={t('Video preview: {file}', { file: file.filename })}
          onError={(event) => {
            const code = event.currentTarget.error?.code;
            if (!compatible && (code === 3 || code === 4)) setCompatible(true);
            else setFailed(true);
          }}
          onPlay={(event) => {
            document.querySelectorAll<HTMLVideoElement>('video.attachment-video').forEach((video) => {
              if (video !== event.currentTarget) video.pause();
            });
          }}>
          <a href={fileUrl(file)} download>{t('Download')}</a>
        </video>
      )}
      <div className="file-video-actions">
        <button className="link small" onClick={onClose}>{t('Hide video')}</button>
        {(compatible || error) && <a href={fileUrl(file)} download>{t('Download original')}</a>}
      </div>
    </>
  );
}

/**
 * The first frame of a video that isn't playing, as a still picture behind the play button. Loaded once it scrolls
 * into view, with "#t=0.1" so browsers show that frame; the server answers range requests, so only the start of the
 * file is fetched. If the browser can't decode the video, the plain play button stays.
 */
function VideoPoster({ file }: { file: Attachment }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [near, setNear] = useState(false);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === 'undefined') return setNear(true);
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && setNear(true), { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [near]);
  if (state === 'failed') return null;
  return (
    <video ref={ref} className={`file-video-poster ${state === 'ready' ? 'ready' : ''}`} src={near ? `${fileUrl(file, true)}#t=0.1` : undefined}
      muted playsInline preload="metadata" tabIndex={-1} aria-hidden="true"
      onLoadedData={() => setState('ready')} onError={() => setState('failed')} />
  );
}

function ImageStage({ file, zoom, onClose }: { file: Attachment; zoom: boolean; onClose: () => void }) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  return (
    <div className={`image-preview-stage${zoom ? ' is-zoomed' : ''}`}>
      {status === 'loading' && <p className="image-preview-status" role="status">{t('Loading image…')}</p>}
      {status === 'error' ? (
        <p role="alert">{t('Could not load this image. You can still download the file.')}</p>
      ) : (
        <img
          src={fileUrl(file, true)}
          alt={file.filename}
          role="button"
          tabIndex={0}
          aria-label={t('Close image preview')}
          title={t('Close image preview')}
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClose();
            }
          }}
          onLoad={() => setStatus('ready')}
          onError={() => setStatus('error')}
        />
      )}
    </div>
  );
}

function ImagePreview({ images, index, onSelect, onClose }: { images: Attachment[]; index: number; onSelect: (id: number) => void; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [zoom, setZoom] = useState(false);
  const file = images[index];
  useEffect(() => {
    const dialog = ref.current!;
    dialog.showModal();
    closeRef.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      dialog.close();
      document.body.style.overflow = overflow;
    };
  }, []);
  useEffect(() => setZoom(false), [file.id]);
  const move = (step: number) => {
    const next = images[index + step];
    if (next) onSelect(next.id);
  };
  return createPortal(
    <dialog
      ref={ref}
      className="image-preview"
      aria-label={t('Image preview')}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          move(e.key === 'ArrowLeft' ? -1 : 1);
        }
      }}
    >
      <header className="image-preview-head">
        <div className="image-preview-name"><strong>{file.filename}</strong><span className="subtle small">{fmtBytes(file.filesize)}</span></div>
        <div className="image-preview-actions">
          {images.length > 1 && <>
            <button className="icon-btn" aria-label={t('Previous image')} title={t('Previous image')} disabled={index === 0} onClick={() => move(-1)}>‹</button>
            <span className="small">{index + 1} / {images.length}</span>
            <button className="icon-btn" aria-label={t('Next image')} title={t('Next image')} disabled={index === images.length - 1} onClick={() => move(1)}>›</button>
          </>}
          <button className="btn" aria-pressed={zoom} onClick={() => setZoom(!zoom)}>{zoom ? t('Fit to screen') : t('Actual size')}</button>
          <a className="btn" href={fileUrl(file)} download>{t('Download')}</a>
          <button ref={closeRef} className="icon-btn" aria-label={t('Close')} title={t('Close')} onClick={onClose}>✕</button>
        </div>
      </header>
      <ImageStage key={file.id} file={file} zoom={zoom} onClose={onClose} />
    </dialog>,
    document.body,
  );
}

/** `onMove(id, afterId)`: given, files can be dragged by their icon into another order (afterId null: first). */
export function FileList({ files, canDelete, onDelete, onMove }: {
  files: Attachment[];
  canDelete: (a: Attachment) => boolean;
  onDelete: (a: Attachment) => void;
  onMove?: (id: number, afterId: number | null) => void;
}) {
  const { meta } = useMeta();
  const drag = useDragReorder(files.map((f) => f.id), (id, order) => onMove?.(id, afterId(order, id)), !!onMove);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [videoId, setVideoId] = useState<number | null>(null);
  const images = files.filter(isImage);
  const index = images.findIndex((a) => a.id === previewId);
  if (files.length === 0) return null;
  return (
    <div className="files" style={{ '--attachment-preview-size': `${attachmentPreviewSize(meta.prefs?.attachment_preview_size)}px` } as CSSProperties}>
      {files.map((a) => {
        const previewable = isImage(a);
        const video = isVideo(a);
        const target = drag.target(a.id);
        const handle = drag.handle(a.id);
        return (
          <div key={a.id} {...target} className={`file ${target.className}`.trim()}>
            <div className="file-line">
              <span {...handle} className={`file-icon ${handle.className ?? ''}`.trim()} title={handle.draggable ? t('Drag to change the order') : undefined}>{previewable ? '🖼' : video ? '▶' : /pdf$/i.test(a.file_type) ? '📕' : '📄'}</span>
              {a.available ? previewable || video ? (
                <button className="file-preview-link" title={previewable ? t('Preview image') : t('Preview video')}
                  onClick={() => previewable ? setPreviewId(a.id) : setVideoId(a.id)}>{a.filename}</button>
              ) : (
                <a href={fileUrl(a)}>{a.filename}</a>
              ) : (
                <span title={t('The file content was not imported from Mantis')}>{a.filename} ({t('file missing')})</span>
              )}
              <span className="subtle small">({fmtBytes(a.filesize)}) {fmtDate(a.created_at)}</span>
              {(previewable || video) && <>
                <a className="icon-btn" href={fileUrl(a)} download aria-label={t('Download {file}', { file: a.filename })} title={t('Download')}>↓</a>
                {previewable && <button className="icon-btn" title={t('Copy as Markdown, to show the image in a text')} onClick={() => void navigator.clipboard?.writeText(`![${a.filename}](${fileUrl(a, true)})`).catch(() => {})}>⧉</button>}
              </>}
              {canDelete(a) && <button className="icon-btn danger" title={t('Delete')} onClick={() => confirm(t('Delete {file}?', { file: a.filename })) && onDelete(a)}>🗑</button>}
            </div>
            {previewable && <button className="file-preview-thumb" aria-label={t('Preview {file}', { file: a.filename })} onClick={() => setPreviewId(a.id)}>
              <img className="thumb" src={fileUrl(a, true)} alt={a.filename} loading="lazy" />
            </button>}
            {video && <div className="file-video">
              {videoId === a.id ? <VideoPreview file={a} onClose={() => setVideoId(null)} /> : (
                <button className="file-video-trigger" aria-label={t('Play {file}', { file: a.filename })} onClick={() => setVideoId(a.id)}>
                  <VideoPoster file={a} />
                  <span className="file-video-play" aria-hidden="true">▶</span>
                  <span className="file-video-label">{t('Play video')}</span>
                </button>
              )}
            </div>}
          </div>
        );
      })}
      {index >= 0 && <ImagePreview images={images} index={index} onSelect={setPreviewId} onClose={() => setPreviewId(null)} />}
    </div>
  );
}
